import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { toast } from 'sonner';
import { clearAppCaches } from '@/utils/clearAppCaches';

const STORAGE_KEY = 'app_web_build_time';
const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 min

interface VersionInfo {
  version: string;
  buildTime: number;
}

function isLovablePreview(): boolean {
  try {
    if (window.self !== window.top) return true;
  } catch {
    return true; // cross-origin iframe → preview
  }
  const host = window.location.hostname;
  return (
    host.startsWith('id-preview--') ||
    host.startsWith('preview--') ||
    host.endsWith('.lovableproject.com') ||
    host.endsWith('.lovableproject-dev.com') ||
    host.endsWith('.beta.lovable.dev')
  );
}

async function fetchServerBuildTime(): Promise<number | null> {
  try {
    const res = await fetch(`/version.json?t=${Date.now()}`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
      },
    });
    if (!res.ok) return null;
    const data: VersionInfo = await res.json();
    return typeof data.buildTime === 'number' ? data.buildTime : null;
  } catch {
    return null;
  }
}

/**
 * Polling de version pour le web (PWA / browser).
 * - check au mount → reload immédiat (page fraîche, rien à perdre)
 * - check à chaque retour d'onglet et toutes les 5 min → PAS de reload
 *   d'autorité (l'utilisateur peut être en pleine saisie) : toast persistant
 *   avec action « Actualiser » ; la version stockée n'avance qu'au reload
 *   effectif, donc une mise à jour ignorée s'applique au prochain chargement.
 *
 * Désactivé en natif (Capacitor a son propre check via useCapacitorLifecycle).
 * Désactivé en preview Lovable pour ne pas perturber l'éditeur.
 */
export function useVersionPolling() {
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return;
    if (isLovablePreview()) return;

    let cancelled = false;
    let interval: ReturnType<typeof setInterval> | null = null;
    let promptedBuildTime: number | null = null;

    const applyUpdate = async (serverBuildTime: number) => {
      localStorage.setItem(STORAGE_KEY, String(serverBuildTime));
      await clearAppCaches();
      const path = window.location.pathname + window.location.hash;
      window.location.replace(`${path}${path.includes('?') ? '&' : '?'}_v=${serverBuildTime}`);
    };

    const check = async ({ allowImmediateReload }: { allowImmediateReload: boolean }) => {
      const serverBuildTime = await fetchServerBuildTime();
      if (cancelled || serverBuildTime == null) return;

      const storedRaw = localStorage.getItem(STORAGE_KEY);
      const stored = storedRaw ? parseInt(storedRaw, 10) : null;

      if (stored == null) {
        localStorage.setItem(STORAGE_KEY, String(serverBuildTime));
        return;
      }

      if (serverBuildTime <= stored) return;

      if (allowImmediateReload) {
        console.log('[useVersionPolling] New version detected at load, reloading...', {
          stored,
          server: serverBuildTime,
        });
        await applyUpdate(serverBuildTime);
        return;
      }

      if (promptedBuildTime === serverBuildTime) return;
      promptedBuildTime = serverBuildTime;
      console.log('[useVersionPolling] New version detected mid-session — deferring to user');
      toast.info('Nouvelle version disponible', {
        description: "Actualisez quand vous voulez — elle s'appliquera aussi au prochain chargement.",
        duration: Infinity,
        action: {
          label: 'Actualiser',
          onClick: () => { applyUpdate(serverBuildTime); },
        },
      });
    };

    // 1. immediate
    check({ allowImmediateReload: true });

    // 2. visibility
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check({ allowImmediateReload: false });
    };
    document.addEventListener('visibilitychange', onVisibility);

    // 3. interval
    interval = setInterval(() => check({ allowImmediateReload: false }), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      if (interval) clearInterval(interval);
    };
  }, []);
}
